import { useState, type ComponentType } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useSearchParams, Link } from "react-router-dom";
import {
  Search,
  History as HistoryIcon,
  ChevronLeft,
  ChevronRight,
  PlusCircle,
  MessageSquare,
  ArrowRightLeft,
  UserPlus,
  Paperclip,
  Trash,
  RefreshCw,
  FolderSync,
  AlertCircle,
  Download,
  FileText,
  type LucideProps,
} from "lucide-react";
import {
  api,
  apiError,
  type Paginated,
  type ActivityLog,
} from "../lib/api";
import { GlassCard } from "../components/ui/GlassCard";
import { EmptyState } from "../components/ui/EmptyState";
import { fmtRelative } from "../lib/format";
import { Avatar } from "../components/ui/Avatar";
import { StatusBadge } from "../components/tickets/StatusBadge";
import { Modal } from "../components/ui/Modal";
import { Spinner } from "../components/ui/FullScreenLoader";
import { cn } from "../lib/cn";
import { toast } from "sonner";

function getActivityIcon(action: string): {
  icon: ComponentType<LucideProps>;
  color: string;
} {
  const act = action.toLowerCase();
  if (act.includes("created"))
    return { icon: PlusCircle, color: "text-emerald-600 dark:text-emerald-400 bg-emerald-500/20 dark:bg-emerald-500/30 border-emerald-500/35 dark:border-emerald-500/50" };
  if (act.includes("comment") || act.includes("note"))
    return { icon: MessageSquare, color: "text-sky-600 dark:text-sky-400 bg-sky-500/20 dark:bg-sky-500/30 border-sky-500/35 dark:border-sky-500/50" };
  if (act.includes("status"))
    return { icon: ArrowRightLeft, color: "text-amber-600 dark:text-amber-400 bg-amber-500/20 dark:bg-amber-500/30 border-amber-500/35 dark:border-amber-500/50" };
  if (act.includes("assign") || act.includes("unassign"))
    return { icon: UserPlus, color: "text-indigo-600 dark:text-indigo-400 bg-indigo-500/20 dark:bg-indigo-500/30 border-indigo-500/35 dark:border-indigo-500/50" };
  if (act.includes("attachment") || act.includes("file"))
    return { icon: Paperclip, color: "text-pink-600 dark:text-pink-400 bg-pink-500/20 dark:bg-pink-500/30 border-pink-500/35 dark:border-pink-500/50" };
  if (act.includes("recycle") || act.includes("delete"))
    return { icon: Trash, color: "text-rose-600 dark:text-rose-400 bg-rose-500/20 dark:bg-rose-500/30 border-rose-500/35 dark:border-rose-500/50" };
  if (act.includes("restored"))
    return { icon: RefreshCw, color: "text-teal-600 dark:text-teal-400 bg-teal-500/20 dark:bg-teal-500/30 border-teal-500/35 dark:border-teal-500/50" };
  if (act.includes("priority"))
    return { icon: AlertCircle, color: "text-purple-600 dark:text-purple-400 bg-purple-500/20 dark:bg-purple-500/30 border-purple-500/35 dark:border-purple-500/50" };
  return { icon: FolderSync, color: "text-indigo-600 dark:text-indigo-400 bg-indigo-500/20 dark:bg-indigo-500/30 border-indigo-500/35 dark:border-indigo-500/50" };
}

let searchDebounce: ReturnType<typeof setTimeout> | undefined;

export default function History() {
  const [search, setSearch] = useSearchParams();
  const q = search.get("q") || "";
  const page = parseInt(search.get("page") || "1", 10);
  const [exporting, setExporting] = useState(false);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(search);
    if (!value) {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    if (key !== "page") next.delete("page");
    setSearch(next, { replace: true });
  };

  const { data, isLoading } = useQuery({
    queryKey: ["tickets.history", { q, page }],
    queryFn: async () => {
      const res = await api.get<Paginated<ActivityLog>>("/api/tickets/history", {
        params: { q, page, pageSize: 15 },
      });
      return res.data;
    },
    placeholderData: keepPreviousData,
  });

  const items = data?.items || [];
  const total = data?.total || 0;
  const totalPages = data?.totalPages || 1;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h1 className="page-title">Global Ticketing History</h1>
          <p className="page-subtitle">
            Timeline of all operations and ticket updates across the desk
          </p>
        </div>

        <div className="flex items-center gap-3 w-full md:w-auto flex-wrap">
          <div className="relative w-full sm:w-80 md:w-96 flex-1 sm:flex-initial">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search action or ticket..."
              defaultValue={q}
              onChange={(e) => {
                const v = e.target.value;
                clearTimeout(searchDebounce);
                searchDebounce = setTimeout(() => updateParam("q", v), 350);
              }}
              className="input pl-10"
            />
          </div>
          <button
            onClick={() => setExporting(true)}
            className="btn-secondary flex items-center gap-1.5 whitespace-nowrap shrink-0"
            title="Export History Logs"
            disabled={isLoading || items.length === 0}
          >
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="skeleton h-20 rounded-2xl" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <GlassCard>
          <EmptyState
            icon={HistoryIcon}
            title="No history found"
            description={
              q
                ? "No logs match your search. Try clearing filters."
                : "No operations have been logged yet."
            }
          />
        </GlassCard>
      ) : (
        <div className="relative border-l border-white/10 ml-6 pl-6 space-y-6">
          {items.map((item) => {
            const { icon: Icon, color } = getActivityIcon(item.action);
            return (
              <div key={item.id} className="relative group">
                {/* Timeline dot */}
                <div
                  className={`absolute -left-10 top-2 w-8 h-8 rounded-full border flex items-center justify-center transition-all group-hover:scale-110 shadow-lg ${color}`}
                >
                  <Icon className="w-4 h-4" strokeWidth={2.5} />
                </div>

                {/* Event Card */}
                <GlassCard className="transition-all hover:bg-white/[0.04] p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="space-y-1 flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-white leading-none">
                        {item.action}
                      </span>
                      {item.ticket && (
                        <Link
                          to={`/tickets/${item.ticketId}`}
                          className="text-xs text-brand-400 hover:text-brand-300 font-medium underline truncate max-w-[250px]"
                        >
                          #{item.ticketId.slice(-6)}: {item.ticket.title}
                        </Link>
                      )}
                    </div>

                    <div className="flex items-center gap-2 text-xs text-slate-400">
                      <Avatar name={item.user?.name || "?"} url={item.user?.avatarUrl} size="xs" />
                      <span>{item.user?.name}</span>
                      <span className="text-[10px] uppercase tracking-wider text-slate-500 bg-white/5 px-1.5 py-0.5 rounded">
                        {item.user?.role}
                      </span>
                      <span>·</span>
                      <span>{fmtRelative(item.createdAt)}</span>
                    </div>

                    {item.meta &&
                      typeof item.meta === "object" &&
                      Object.keys(item.meta).length > 0 && (
                        <div className="text-[11px] text-slate-500 bg-black/10 rounded-lg p-2 mt-2 font-mono flex flex-wrap gap-x-4 gap-y-1">
                          {Object.entries(item.meta).map(([key, val]) => (
                            <div key={key}>
                              <span className="text-slate-400 font-semibold">
                                {key}:
                              </span>{" "}
                              {String(val)}
                            </div>
                          ))}
                        </div>
                      )}
                  </div>

                  {item.ticket && (
                    <div className="shrink-0 flex items-center gap-3">
                      <StatusBadge status={item.ticket.status} />
                      <Link
                        to={`/tickets/${item.ticketId}`}
                        className="btn-secondary py-1 px-3 text-xs"
                      >
                        View Ticket
                      </Link>
                    </div>
                  )}
                </GlassCard>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-4">
          <button
            disabled={page <= 1}
            onClick={() => updateParam("page", String(page - 1))}
            className="btn-ghost disabled:opacity-40"
          >
            <ChevronLeft className="w-4 h-4" /> Prev
          </button>
          <span className="text-sm text-slate-300 px-3">
            Page <b className="text-white">{page}</b> of {totalPages} (Total:{" "}
            {total})
          </span>
          <button
            disabled={page >= totalPages}
            onClick={() => updateParam("page", String(page + 1))}
            className="btn-ghost disabled:opacity-40"
          >
            Next <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Export Modal */}
      <ExportHistoryModal
        open={exporting}
        onClose={() => setExporting(false)}
        q={q}
      />
    </div>
  );
}

// ===================== Import & Export Utilities =====================

function downloadCSV(logs: ActivityLog[]) {
  const headers = [
    "Log ID",
    "Action",
    "Ticket ID",
    "Ticket Title",
    "Ticket Status",
    "User Name",
    "User Email",
    "User Role",
    "Date/Time",
    "Metadata",
  ];
  const rows = logs.map((item) => [
    `"${item.id}"`,
    `"${(item.action || "").replace(/"/g, '""')}"`,
    `"${item.ticketId || ""}"`,
    `"${(item.ticket?.title || "").replace(/"/g, '""')}"`,
    `"${item.ticket?.status || ""}"`,
    `"${(item.user?.name || "").replace(/"/g, '""')}"`,
    `"${(item.user?.email || "").replace(/"/g, '""')}"`,
    `"${item.user?.role || ""}"`,
    `"${item.createdAt}"`,
    `"${item.meta ? JSON.stringify(item.meta).replace(/"/g, '""') : ""}"`,
  ]);
  const csvContent = [headers.join(","), ...rows.map((r) => r.join(","))].join(
    "\n",
  );
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute(
    "download",
    `impact_history_${new Date().toISOString().split("T")[0]}.csv`,
  );
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function downloadJSON(logs: ActivityLog[]) {
  const content = JSON.stringify(
    logs.map((item) => ({
      id: item.id,
      action: item.action,
      ticketId: item.ticketId,
      ticketTitle: item.ticket?.title,
      ticketStatus: item.ticket?.status,
      userName: item.user?.name,
      userEmail: item.user?.email,
      userRole: item.user?.role,
      createdAt: item.createdAt,
      metadata: item.meta,
    })),
    null,
    2,
  );
  const blob = new Blob([content], { type: "application/json;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute(
    "download",
    `impact_history_${new Date().toISOString().split("T")[0]}.json`,
  );
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function ExportHistoryModal({
  open,
  onClose,
  q,
}: {
  open: boolean;
  onClose: () => void;
  q: string;
}) {
  const [format, setFormat] = useState<"csv" | "json">("csv");
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      let logsToExport: ActivityLog[] = [];
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const res = await api.get<Paginated<ActivityLog>>(
          "/api/tickets/history",
          { params: { q, page, pageSize: 100 } },
        );
        const items = res.data?.items || [];
        logsToExport = [...logsToExport, ...items];

        const totalPages = res.data?.totalPages || 1;
        if (page >= totalPages || items.length < 100) {
          hasMore = false;
        } else {
          page++;
        }
      }

      if (!logsToExport.length) {
        toast.error("No history records found to export");
        onClose();
        return;
      }

      if (format === "csv") {
        downloadCSV(logsToExport);
      } else {
        downloadJSON(logsToExport);
      }
      toast.success(`Successfully exported ${logsToExport.length} history records`);
      onClose();
    } catch (e) {
      toast.error("Failed to export history: " + apiError(e));
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Export Ticketing History">
      <div className="space-y-4">
        <p className="text-sm text-slate-300">
          Export global activity history. The export will include logged actions,
          ticket details, performer information, timestamps, and log metadata.
        </p>

        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => setFormat("csv")}
            className={cn(
              "p-4 rounded-2xl border flex flex-col items-center justify-center gap-2 transition-all",
              format === "csv"
                ? "bg-white/10 border-brand-500/40 text-brand-200"
                : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10",
            )}
          >
            <FileText className="w-8 h-8 text-brand-400" />
            <span className="font-semibold text-sm">CSV Format</span>
            <span className="text-[10px] text-slate-400">
              Great for Excel & Sheets
            </span>
          </button>

          <button
            onClick={() => setFormat("json")}
            className={cn(
              "p-4 rounded-2xl border flex flex-col items-center justify-center gap-2 transition-all",
              format === "json"
                ? "bg-white/10 border-indigo-500/40 text-indigo-200"
                : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10",
            )}
          >
            <FileText className="w-8 h-8 text-indigo-400" />
            <span className="font-semibold text-sm">JSON Format</span>
            <span className="text-[10px] text-slate-400">
              Great for system audits & backups
            </span>
          </button>
        </div>

        <div className="pt-3 flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            onClick={handleExport}
            className="btn-primary flex items-center gap-1.5"
            disabled={isExporting}
          >
            {isExporting ? <Spinner /> : <Download className="w-4 h-4" />}
            Export
          </button>
        </div>
      </div>
    </Modal>
  );
}
